import { generateHTML } from '@tiptap/core'
import type { JSONContent } from '@tiptap/react'
import { getRenderExtensions } from '#src/components/rich-text-editor/extensions.ts'
import {
  extractBlockLinesFromHTML,
  extractFirstLineFromHTML,
  extractTextFromHTML,
} from '#src/utils/html.ts'

/* Detect whether a content string is a TipTap JSON doc or plain HTML.
 * - JSON doc → returns parsed JSONContent
 * - HTML, non-doc JSON, or garbage → returns the original string
 *
 * The `type === 'doc'` defensive check prevents user-typed strings like
 * `{"foo":"bar"}` from being interpreted as TipTap content. */
export function parseNoteContent(content: string): string | JSONContent {
  if (!content.startsWith('{')) {
    return content
  }
  try {
    const parsed = JSON.parse(content)
    if (parsed && typeof parsed === 'object' && parsed.type === 'doc') {
      return parsed as JSONContent
    }
  } catch {
    // fall through
  }
  return content
}

/* Convert any content (JSON or HTML) to HTML. Round-trips through TipTap's
 * `generateHTML` with RENDER_EXTENSIONS for JSON inputs; passes HTML through.
 *
 * Round-tripping (vs walking JSON nodes directly) is required because many
 * custom inline nodes — mentions, observations, choice nodes — need
 * extension-aware HTML serialization that a naive JSON tree walker would drop. */
export function contentToHTML(content: string): string {
  const parsed = parseNoteContent(content)
  if (typeof parsed === 'string') {
    return parsed
  }
  return generateHTML(parsed, getRenderExtensions())
}

/* Extract the first non-empty line of text from note content in either format.
 * Used for titles in timeline, drafts, search, badge hover, editor header. */
export function extractFirstLineFromContent(content: string): string {
  return extractFirstLineFromHTML(contentToHTML(content))
}

/* Extract all visible text from note content in either format. Used for
 * full-text search and other text-only consumers.
 *
 * Known issue: this reads `textContent` off the whole body, so adjacent blocks
 * fuse into one token — `<p>hello</p><p>yay</p>` yields `helloyay`, which no
 * query for either word matches. `extractBlockLinesFromContent` (joined with
 * whitespace) fixes that, but swapping this implementation would change
 * note-search results on the shared notes page outside any flag, so callers
 * opt in one at a time. */
export function extractTextFromContent(content: string): string {
  return extractTextFromHTML(contentToHTML(content))
}

/* Note content as one entry per block, blank blocks dropped. Callers that need
 * both a title and a body preview should use this and split it, so the two
 * partition the same text; callers indexing a whole note should join the lines
 * so adjacent blocks don't fuse into one search token. */
export function extractBlockLinesFromContent(content: string): string[] {
  return extractBlockLinesFromHTML(contentToHTML(content))
}
